/**
 * LinkedIn Verification Redirect Component
 *
 * This component initiates the LinkedIn OAuth flow for survey access verification.
 * It redirects users to the backend endpoint that handles LinkedIn authorization.
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Linkedin, Loader2, AlertTriangle } from 'lucide-react';

const LinkedInVerificationRedirect: React.FC = () => {
  const { shareId } = useParams<{ shareId: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!shareId) {
      setError('Invalid survey link');
      return;
    }

    // Redirect to backend LinkedIn authorization endpoint
    const authUrl = `${import.meta.env.VITE_API_URL}/api/linkedin/authorize/${shareId}`;
    window.location.href = authUrl;
  }, [shareId]);

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Invalid Link</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
        <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
          <Linkedin className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-xl font-semibold text-gray-900 mb-2">Connecting to LinkedIn</h1>
        <p className="text-gray-600 mb-6">
          Please wait while we redirect you to LinkedIn to verify your profile...
        </p>
        <div className="flex items-center justify-center gap-2 text-blue-600">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm font-medium">Redirecting...</span>
        </div>
      </div>
    </div>
  );
};

export default LinkedInVerificationRedirect;
